import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../../pages/aai/useAsync';
import type { McpPermissionsView, McpToolDecision, McpToolPermission } from '../../types/aai';
import { Action, Chip, EmptyState, ErrorNote, Kicker, Loading, Modal, Select } from '../aa';

/*
 * Settings > MCP > <server> > Permissions. Each person says, tool by tool, what
 * the AI may do in their own chats. What they allow runs without asking again;
 * what they turn off never runs. A tool left on the default runs when it
 * reads, and in a chat asks before a change.
 */

const CHOICE_LABEL: Record<McpToolDecision, string> = {
  yes: 'Runs on its own',
  approval: 'Asks each time',
  no: 'Off',
};

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const defaultLabel = (tool: McpToolPermission): string => {
  if (tool.read_only) return 'Default: runs on its own';
  return 'Default: reads run, changes ask in chat';
};

export const McpPermissions: React.FC<{ server: { key: string; label: string }; onClose: () => void }> = ({ server, onClose }) => {
  const view = useAsync<McpPermissionsView>(() => aaiApi.getMcpPermissions(server.key), `Could not load the ${server.label} tools.`);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState('');
  const [changes, setChanges] = useState<Record<string, McpToolDecision | null>>({});

  const tools = useMemo(() => (view.data?.tools || []).map((t) => {
    if (t.name in changes) return { ...t, decision: changes[t.name] };
    return t;
  }), [view.data, changes]);
  const needle = query.trim().toLowerCase();
  const shown = tools.filter((t) => !needle || `${t.title} ${t.name} ${t.description}`.toLowerCase().includes(needle));
  const changeTools = tools.filter((t) => !t.read_only);

  const save = async (tool: McpToolPermission, decision: McpToolDecision | null) => {
    setSaving(tool.name);
    try {
      await aaiApi.setMcpPermission(server.key, { tool: tool.name, decision, title: tool.title, read_only: tool.read_only });
      setChanges((c) => ({ ...c, [tool.name]: decision }));
    } catch (err) {
      toast.error(detailOf(err, `Could not save ${tool.title}.`));
    } finally {
      setSaving('');
    }
  };

  const setAllChanges = async (decision: McpToolDecision) => {
    setSaving('*');
    try {
      for (const tool of changeTools) {
        if (tool.destructive && decision === 'yes') continue;
        await aaiApi.setMcpPermission(server.key, { tool: tool.name, decision, title: tool.title, read_only: false });
        setChanges((c) => ({ ...c, [tool.name]: decision }));
      }
      toast.success(`Every ${server.label} tool that changes things: ${CHOICE_LABEL[decision].toLowerCase()}.`);
    } catch (err) {
      toast.error(detailOf(err, 'Could not save every tool.'));
    } finally {
      setSaving('');
    }
  };

  const onPick = (tool: McpToolPermission, value: string) => {
    if (value === 'default') {
      save(tool, null);
      return;
    }
    save(tool, value as McpToolDecision);
  };

  let body: React.ReactNode = null;
  if (view.loading) body = <Loading label={`Asking ${server.label} for its tools`} />;
  if (!view.loading && !view.data) body = <ErrorNote message={view.error || `Could not load the ${server.label} tools.`} />;
  if (view.data) {
    body = (
      <>
        {view.data.error && (
          <div className="mb-4">
            <ErrorNote message={`${server.label} could not list its tools right now: ${view.data.error}`} />
          </div>
        )}
        {tools.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools"
                aria-label={`Search ${server.label} tools`}
                className="h-10 w-full rounded-act border border-line bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
              />
            </span>
            {changeTools.length > 0 && (
              <Select
                aria-label="Set every tool that changes things"
                className="!w-auto"
                value=""
                disabled={saving !== ''}
                onChange={(e) => { if (e.target.value) setAllChanges(e.target.value as McpToolDecision); }}
              >
                <option value="">Every change tool…</option>
                <option value="yes">Runs on its own (not deletes)</option>
                <option value="approval">Asks each time</option>
                <option value="no">Off</option>
              </Select>
            )}
          </div>
        )}
        {tools.length === 0 && !view.data.error && (
          <EmptyState title="No tools listed" reason={`${server.label} did not list any tools.`} />
        )}
        {tools.length > 0 && shown.length === 0 && (
          <EmptyState title={`Nothing matches "${query}"`} reason="Try another word." />
        )}
        <div className="divide-y divide-line-soft">
          {shown.map((tool) => {
            let picked = 'default';
            if (tool.decision) picked = tool.decision;
            return (
              <div key={tool.name} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-ink">{tool.title}</span>
                    {tool.read_only && <Chip>Reads only</Chip>}
                    {tool.destructive && <Chip tone="warn">Deletes</Chip>}
                    {tool.unlisted && <Chip>No longer listed</Chip>}
                  </div>
                  {tool.description && <p className="m-0 mt-0.5 line-clamp-2 text-[12.5px] leading-[18px] text-ink-muted">{tool.description}</p>}
                </div>
                <Select
                  aria-label={`What the AI may do with ${tool.title}`}
                  className="sm:!w-[230px]"
                  value={picked}
                  disabled={saving !== ''}
                  onChange={(e) => onPick(tool, e.target.value)}
                >
                  <option value="default">{defaultLabel(tool)}</option>
                  <option value="yes">{CHOICE_LABEL.yes}</option>
                  <option value="approval">{CHOICE_LABEL.approval}</option>
                  <option value="no">{CHOICE_LABEL.no}</option>
                </Select>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <Modal
      size="wide"
      onClose={onClose}
      context={<Kicker>Permissions</Kicker>}
      footer={<div className="flex justify-end"><Action variant="quiet" onClick={onClose}>Done</Action></div>}
    >
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">What the AI may do in {server.label}</h2>
      <p className="m-0 mb-5 mt-2 text-[14px] leading-[23px] text-ink-muted">
        For your own chats. What you allow runs without asking again; what you turn off never runs. Saved as you choose.
      </p>
      {body}
    </Modal>
  );
};

export default McpPermissions;
